import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from 'hooks';
import { useNavigate } from 'react-router-dom';

import { isInvoiceBillingType } from '~constants/maps';

import { ContainerHeader, ErrorResponse } from '~components';
import { faPlus } from '~components/icon';
import { Form as WizardForm } from '~components/wizard-form';

import { Budget } from '~support-at-home/models';

import newBudgetFields from '~support-at-home/forms/budgets/newBudget';

import { getAll as getClients } from '~main/actions/clients';
import { useSaveBudgetMutation } from '~support-at-home/actions/budgets';
import { getAll as getClientBudgetStatuses } from '~support-at-home/actions/clientBudgetStatuses';

import { toggleQuickMenu } from '~main/reducers/app';

const BudgetNew: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [saveBudget] = useSaveBudgetMutation();
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(getClients());
    dispatch(getClientBudgetStatuses());
    dispatch(toggleQuickMenu(true));

    return () => {
      dispatch(toggleQuickMenu(false));
    };
  }, []);

  /** initial form values */
  const initialValues = useMemo(
    () =>
      ({
        sections: [],
        total_amount: 0,
        total_allocated: 0,
        total_includes_case_management: undefined,
        ignore_sah_filters: false,
      }) as Partial<Budget>,
    [],
  );

  const onAddBudget = async (values: Budget) => {
    setSubmitError(null);
    const newBudget = {
      client_id: values.client_id,
      client_sah_budget_id: values.client_sah_budget_id,
      price_plan_revision_id: values.price_plan_revision_id,
      client_package_id: parseInt((values as any).package_fixed_id, 10),
      total_includes_case_management: values.total_includes_case_management,
      ignore_sah_filters: values.ignore_sah_filters,
      supplements: values.supplements,
      start_date: values.start_date,
      status_id: values.status_id,
      end_date: values.end_date,
      total_amount: values.total_amount,
      sections: values.sections,
    };

    try {
      const result = await saveBudget(newBudget).unwrap();
      if (result?.id) {
        navigate('/support-at-home/budgets');
        return true;
      }
    } catch (err: any) {
      const errorMessage = err?.data?.message ?? 'An error occurred while creating the budget.';
      setSubmitError(errorMessage);
    }
    return false;
  };

  const onCancelBudget = () => navigate('/support-at-home/budgets');

  const beforeStepChange = async (step: number, values: Budget) => {
    if (step === 4) {
      const hasInvalidSection =
        values.sections?.some((section) =>
          section.items?.some((item) => {
            const { billing_type, start_date, end_date, shift_id, frequency_id } = item;
            if (!start_date || !end_date) return true;
            if (!billing_type || !isInvoiceBillingType(billing_type)) {
              return !shift_id || !frequency_id;
            }
            return false;
          }),
        ) ?? false;

      if (hasInvalidSection) {
        alert(
          'All chosen service types must have valid start/end dates. Shift & frequency must also be selected if available.',
        );
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return false;
      }
    }
    return true;
  };

  return (
    <div className="p-2 budget">
      <ContainerHeader icon={faPlus} iconSize="1x" className="info" dataCy="new-budgets-header">
        New Budget
      </ContainerHeader>
      <ErrorResponse message={submitError} dataCy="new-budget-submit-error" />
      <WizardForm
        steps={newBudgetFields}
        initialValues={initialValues as unknown as Budget}
        bodyClassname="d-flex flex-column flex-grow-1"
        onComplete={onAddBudget}
        onCancel={onCancelBudget}
        beforeStepChange={beforeStepChange}
      />
    </div>
  );
};

export default BudgetNew;
